import { isFeatureEnabled as isCcmsFeatureToggleEnabled } from '@shopee-user/static-feature-toggle';
import { Feature as CcmsFeature } from '@shopee-user/static-feature-toggle';

import {
  isWebBridgeSupported,
  getPlatformType,
  PlatformType,
} from '@shopee-user/platform';

import { isFeatureEnabled as isHardcodedFeatureToggleEnabled } from '@shopee-user/business-customization-logic';
import { Feature as HardcodedFeature } from '@shopee-user/feature-toggle';
import { AuthenticateChannel } from './core/types';
import { getWebThirdPartyClientId } from '@shopee-user/business-customization-logic';
import { AuthMethod } from '@shopee/web-bridge-sdk';

export const isSupportShopeeOAuth = () => {
  if (isWebBridgeSupported()) {
    return (
      isHardcodedFeatureToggleEnabled(
        HardcodedFeature.ThirdpartyIntegrationSupportShopeeInApp,
        false
      ) &&
      isCcmsFeatureToggleEnabled(
        CcmsFeature.EnableThirdpartyIntegrationSupportShopeeInApp,
        false
      )
    );
  }
  return (
    isHardcodedFeatureToggleEnabled(
      HardcodedFeature.ThirdpartyIntegrationSupportShopeeInWeb,
      false
    ) &&
    isCcmsFeatureToggleEnabled(
      CcmsFeature.EnableThirdpartyIntegrationSupportShopeeInWeb,
      false
    )
  );
};

export const isSupportGoogleOAuth = () => {
  const isWebClientIdDefined =
    getWebThirdPartyClientId({ channel: AuthenticateChannel.Google }) !== '';
  if (isWebBridgeSupported()) {
    return (
      isHardcodedFeatureToggleEnabled(
        HardcodedFeature.ThirdpartyIntegrationSupportGoogleInApp,
        false
      ) &&
      isCcmsFeatureToggleEnabled(
        CcmsFeature.EnableThirdpartyIntegrationSupportGoogleInApp,
        false
      ) &&
      isWebClientIdDefined
    );
  }
  return (
    isHardcodedFeatureToggleEnabled(
      HardcodedFeature.ThirdpartyIntegrationSupportGoogleInWeb,
      false
    ) &&
    isCcmsFeatureToggleEnabled(
      CcmsFeature.EnableThirdpartyIntegrationSupportGoogleInWeb,
      false
    ) &&
    isWebClientIdDefined
  );
};

export const isSupportFacebookOAuth = () => {
  const isWebClientIdDefined =
    getWebThirdPartyClientId({ channel: AuthenticateChannel.Facebook }) !== '';
  if (isWebBridgeSupported()) {
    return (
      isHardcodedFeatureToggleEnabled(
        HardcodedFeature.ThirdpartyIntegrationSupportFacebookInApp,
        false
      ) &&
      isCcmsFeatureToggleEnabled(
        CcmsFeature.EnableThirdpartyIntegrationSupportFacebookInApp,
        false
      ) &&
      isWebClientIdDefined
    );
  }
  return (
    isHardcodedFeatureToggleEnabled(
      HardcodedFeature.ThirdpartyIntegrationSupportFacebookInWeb,
      false
    ) &&
    isCcmsFeatureToggleEnabled(
      CcmsFeature.EnableThirdpartyIntegrationSupportFacebookInWeb,
      false
    ) &&
    isWebClientIdDefined
  );
};

/**
 * Only supported in iOS App: https://confluence.shopee.io/x/gjTdpg
 */
export const isSupportAppleOAuth = () => {
  const isIOS = getPlatformType() === PlatformType.iOS;
  if (!isWebBridgeSupported()) {
    return false;
  }

  return (
    isHardcodedFeatureToggleEnabled(
      HardcodedFeature.ThirdpartyIntegrationSupportAppleInApp,
      false
    ) &&
    isCcmsFeatureToggleEnabled(
      CcmsFeature.EnableThirdpartyIntegrationSupportAppleInApp,
      false
    ) &&
    isIOS
  );
};

export const isSupportAppleLinkage = () => {
  const isWebClientIdDefined =
    getWebThirdPartyClientId({ channel: AuthenticateChannel.Apple }) !== '';
  const isIOS = getPlatformType() === PlatformType.iOS;
  if (isWebBridgeSupported() && isWebClientIdDefined && isIOS) {
    return (
      isHardcodedFeatureToggleEnabled(
        HardcodedFeature.ThirdpartyIntegrationSupportAppleInApp,
        false
      ) &&
      isCcmsFeatureToggleEnabled(
        CcmsFeature.EnableThirdpartyIntegrationSupportAppleInApp,
        false
      ) &&
      isWebClientIdDefined
    );
  }
  return (
    isHardcodedFeatureToggleEnabled(
      HardcodedFeature.ThirdpartyIntegrationSupportAppleInWeb,
      false
    ) &&
    isCcmsFeatureToggleEnabled(
      CcmsFeature.EnableThirdpartyIntegrationSupportAppleInWeb,
      false
    ) &&
    isWebClientIdDefined
  );
};

export const isSupportThirdpartyOAuth = () => {
  return (
    isSupportShopeeOAuth() ||
    isSupportGoogleOAuth() ||
    isSupportFacebookOAuth() ||
    isSupportAppleLinkage()
  );
};

export const isMethodAvailableInApp = ({
  method,
  supportedAuthMethodsInApp,
}: {
  method: AuthMethod;
  supportedAuthMethodsInApp: AuthMethod[];
}) => supportedAuthMethodsInApp?.includes(method) ?? false;
